/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { RestaurantCustomer } from "@lemmonhq/types";
import { SegmentSelect, useLocalization } from "@lemmonhq/uikit";
import { CustomerSearchPanel } from "./CustomerSearchPanel";
import { CustomerCreatePanel } from "./CustomerCreatePanel";

type PanelMode = "search" | "create";

export interface CustomerStepProps {
  customerSearch: string;
  onCustomerSearch: (value: string) => void;
  isSearching: boolean;
  searchResults: RestaurantCustomer[];
  searchError: string;
  onSelectCustomer: (customer: RestaurantCustomer) => void;
  selectedCustomer: RestaurantCustomer | null;
  onClearSelectedCustomer: () => void;
  showNewCustomerForm: boolean;
  onToggleNewCustomerForm: (show: boolean) => void;
  newCustomerForm: { name: string; email: string; phone: string };
  onUpdateNewCustomerField: (
    field: "name" | "email" | "phone",
    value: string,
  ) => void;
  newCustomerError: string;
  isCreatingCustomer: boolean;
  onCreateCustomer: () => void;
}

export const CustomerStep = defineComponent<CustomerStepProps>({
  name: "CustomerStep",
  props: [
    "customerSearch",
    "onCustomerSearch",
    "isSearching",
    "searchResults",
    "searchError",
    "onSelectCustomer",
    "selectedCustomer",
    "onClearSelectedCustomer",
    "showNewCustomerForm",
    "onToggleNewCustomerForm",
    "newCustomerForm",
    "onUpdateNewCustomerField",
    "newCustomerError",
    "isCreatingCustomer",
    "onCreateCustomer",
  ],
  setup(props) {
    const { t } = useLocalization();
    const activeMode = computed<PanelMode>(() =>
      props.showNewCustomerForm ? "create" : "search",
    );

    const handleModeChange = (value?: PanelMode) => {
      const next = value ?? "search";
      const shouldShowCreate = next === "create";
      if (shouldShowCreate !== props.showNewCustomerForm) {
        props.onToggleNewCustomerForm(shouldShowCreate);
      } else if (value === undefined && props.showNewCustomerForm) {
        props.onToggleNewCustomerForm(false);
      }
    };

    const renderSelectedCustomer = () => {
      if (!props.selectedCustomer) {
        return null;
      }

      const customer = props.selectedCustomer;
      return (
        <div class="rounded-xl border border-success/30 bg-success/5 p-4 flex items-center justify-between">
          <div class="flex items-center gap-3 min-w-0">
            <div class="avatar placeholder">
              <div class="bg-success/10 text-success rounded-full w-10 h-10 flex items-center justify-center font-semibold">
                {(customer.name || customer.email || "unnamed")
                  .charAt(0)
                  .toUpperCase()}
              </div>
            </div>
            <div class="min-w-0">
              <p class="font-semibold text-success truncate">
                {customer.name || customer.email}
              </p>
              <p class="text-xs text-success/70 truncate">{customer.email}</p>
            </div>
          </div>
          <button
            class="btn btn-ghost btn-sm"
            onClick={props.onClearSelectedCustomer}
          >
            {t("general.words.change")}
          </button>
        </div>
      );
    };

    return () => (
      <div class="card bg-base-100/50/50 shadow-lg border border-base-300">
        <div class="card-body space-y-6">
          <div class="flex">
            <div class="flex flex-wrap items-start justify-between gap-4 flex-1">
              <div>
                <h3 class="text-lg font-semibold mt-0 text-accent">
                  {t("pos.phonein.select_customer")}
                </h3>
              </div>
            </div>
            {props.selectedCustomer ? null : (
              <>
                <SegmentSelect
                  className="max-w-full md:max-w-md"
                  options={[
                    {
                      value: "search",
                      label: (
                        <span class="flex items-center gap-2">
                          <span class="material-symbols-outlined text-base">
                            search
                          </span>
                          {t("pos.phonein.find_customer")}
                        </span>
                      ),
                    },
                    {
                      value: "create",
                      label: (
                        <span class="flex items-center gap-2">
                          <span class="material-symbols-outlined text-base">
                            person_add
                          </span>
                          {t("pos.phonein.new_customer")}
                        </span>
                      ),
                    },
                  ]}
                  value={activeMode.value}
                  onChange={(value) =>
                    handleModeChange(value as PanelMode | undefined)
                  }
                />
              </>
            )}
          </div>

          {props.selectedCustomer ? (
            renderSelectedCustomer()
          ) : (
            <div class="">
              {activeMode.value === "create" ? (
                <CustomerCreatePanel
                  form={props.newCustomerForm}
                  onUpdateField={props.onUpdateNewCustomerField}
                  error={props.newCustomerError}
                  isSubmitting={props.isCreatingCustomer}
                  onSubmit={props.onCreateCustomer}
                  onCancel={() => handleModeChange("search")}
                />
              ) : (
                <CustomerSearchPanel
                  customerSearch={props.customerSearch}
                  onCustomerSearch={props.onCustomerSearch}
                  isSearching={props.isSearching}
                  searchResults={props.searchResults}
                  searchError={props.searchError}
                  onSelectCustomer={props.onSelectCustomer}
                />
              )}
            </div>
          )}
        </div>
      </div>
    );
  },
});
